import React from 'react';
import { QrCode, BookOpen, ChevronDown, FileText } from 'lucide-react';
import { colors, typography, spacing, components, layout, transitions, shadows, radii } from '../styles/shared';

const styles = {
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: layout.header.height,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(12px)',
    borderBottom: `1px solid ${colors.gray[100]}`,
    zIndex: 100,
    transformStyle: 'flat',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-1px',
      left: 0,
      right: 0,
      height: '1px',
      background: `linear-gradient(90deg, 
        ${colors.primary[400]}00 0%, 
        ${colors.primary[400]}66 50%, 
        ${colors.primary[400]}00 100%
      )`,
    },
  },
  headerScrolled: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    boxShadow: shadows.sm,
  },
  container: {
    ...components.container,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
    color: colors.gray[900],
    textDecoration: 'none',
    transition: transitions.bounce,
    padding: `${spacing[2]} 0`,
    '&:hover': {
      color: colors.primary[600],
      transform: 'translateY(-1px)',
    },
  },
  logoIcon: {
    width: '28px',
    height: '28px',
    color: colors.primary[500],
    transition: transitions.bounce,
  },
  logoText: {
    ...typography.h4,
    margin: 0,
    background: `linear-gradient(135deg, ${colors.gray[900]}, ${colors.primary[600]})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.02em',
  },
  button: {
    ...components.button.base,
    ...components.button.primary,
    padding: `${spacing[2]} ${spacing[4]}`,
    gap: spacing[3],
    background: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.primary[600]})`,
    boxShadow: `0 2px 4px ${colors.primary[400]}33`,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 8px ${colors.primary[400]}66`,
      background: `linear-gradient(135deg, ${colors.primary[600]}, ${colors.primary[700]})`,
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: `0 2px 4px ${colors.primary[400]}33`,
    },
  },
  buttonText: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    textDecoration: "none"
  },
  buttonIcon: {
    transition: transitions.bounce,
    '.header-button:hover &': {
      transform: 'translateX(4px)',
    },
  },
  navButtons: {
    display: 'flex',
    gap: spacing[4],
    alignItems: 'center',
  },
  blogButton: {
    ...components.button.base,
    ...components.button.secondary,
    padding: `${spacing[2]} ${spacing[4]}`,
    gap: spacing[3],
    boxShadow: `0 2px 4px ${colors.gray[400]}33`,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 8px ${colors.gray[400]}66`,
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: `0 2px 4px ${colors.gray[400]}33`,
    },
  },
  dropdownContainer: {
    position: 'relative',
    display: 'inline-block',
  },
  dropdownButton: {
    ...components.button.base,
    ...components.button.secondary,
    padding: `${spacing[2]} ${spacing[4]}`,
    gap: spacing[3],
    boxShadow: `0 2px 4px ${colors.gray[400]}33`,
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 8px ${colors.gray[400]}66`,
    },
  },
  dropdownContent: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: spacing[2],
    backgroundColor: 'white',
    minWidth: '200px',
    boxShadow: shadows.lg,
    borderRadius: radii.md,
    border: `1px solid ${colors.gray[200]}`,
    padding: `${spacing[1]} 0`,
    zIndex: 1000,
    opacity: 0,
    visibility: 'hidden',
    transform: 'translateY(-10px)',
    transition: 'all 0.2s ease-in-out',
  },
  dropdownContentVisible: {
    opacity: 1,
    visibility: 'visible',
    transform: 'translateY(0)',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    padding: `${spacing[2]} ${spacing[3]}`,
    color: colors.gray[700],
    textDecoration: 'none',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: colors.gray[50],
      color: colors.primary[600],
    },
  },
};

const Header = () => {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleScroll = () => {
    const generateSection = document.getElementById('generate-section');
    if (generateSection) {
      generateSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <header style={{
      ...styles.header,
      ...(isScrolled && styles.headerScrolled),
    }}>
      <div style={styles.container}>
        <a href="/" style={styles.logo}>
          <QrCode style={styles.logoIcon} />
          <h1 style={styles.logoText}>Bulk QR Generator</h1>
        </a>
        <div style={styles.navButtons}>
          <button 
            className="header-button"
            style={styles.button} 
            onClick={handleScroll}
          >
            <span style={styles.buttonText}>
              Start Generating
              <QrCode size={18} style={styles.buttonIcon} />
            </span>
          </button>
          <a 
            href="https://www.build-automate-ai-repeat.com/blogs"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.blogButton}
          >
            <span style={styles.buttonText}>
              Blog
              <BookOpen size={18} style={styles.buttonIcon} />
            </span>
          </a>
          <div style={styles.dropdownContainer} ref={dropdownRef}>
            <button 
              style={styles.dropdownButton}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span style={styles.buttonText}>
                More Tools
                <ChevronDown size={18} style={{
                  transform: isDropdownOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease-in-out',
                }} />
              </span>
            </button>
            <div style={{
              ...styles.dropdownContent,
              ...(isDropdownOpen && styles.dropdownContentVisible),
            }}>
              <a 
                href="https://bulk-document-generator.netlify.app/"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.dropdownItem}
              >
                <FileText size={18} />
                Bulk Document Generator
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 